import {
  ArrowsClockwise,
  CalendarBlankIcon,
  File,
  Info,
} from '@phosphor-icons/react';
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
} from 'react';
import DatePicker from 'react-datepicker';
import {
  Button,
  buttonStyles,
  cn,
  Modal,
  Select,
  type SelectOption,
} from '@skupreme/shared/ui';
import { DATEPICKER_SCOPE, FIELD_INPUT } from '@elements/form-field/styles';
import { DateFormat, DateUtility } from '@utility/date';
import { DangerText } from '@elements/Common';
import { AppTable, AppTableCell, AppTableRow } from '@components/AppTable';
import { orderExportHook } from '@hooks/order';
import { CommonUtility } from '@utility/common';
import { DarkPopup } from '@elements/Popup';
import { TablePagination } from '@components/TablePagination';
import { OrderStatus } from '@utility/constants/common';

/* "All statuses" is the Select's empty choice, which exports with no status filter. */
const orderStatusOptions: SelectOption[] = [
  OrderStatus.PendingShipment,
  OrderStatus.PendingFulfillment,
  OrderStatus.Shipped,
  OrderStatus.Returns,
  OrderStatus.Canceled,
].map(status => ({ label: status, value: status }));

/* react-datepicker clones this with its own value and handlers. */
const RangeInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ className, ...rest }, ref) => (
  <span className="sk:relative sk:flex sk:w-full sk:items-center">
    <input
      {...rest}
      ref={ref}
      aria-label="Export date range"
      className={cn(FIELD_INPUT, 'sk:pr-8!', className, 'sk:m-0!')}
    />
    <CalendarBlankIcon
      size={14}
      aria-hidden
      className="sk:pointer-events-none sk:absolute sk:right-3 sk:text-fg-tertiary"
    />
  </span>
));
RangeInput.displayName = 'RangeInput';

type ExportOrderPopupProps = {
  open: boolean;
  onClose: () => void;
};

export function ExportOrderPopup({ open, onClose }: ExportOrderPopupProps) {
  const [dpDateRange, setDpDateRange] = useState([new Date(), new Date()]);
  const [exportStatus, setExportStatus] = useState('');
  const previousDateRef = useRef(null);
  const [isInvalidDate, setIsInvalidDate] = useState(true);
  const {
    data: exportHistory,
    fetchData,
    exportOrders,
    loading,
    pageChanged,
    filter,
    filterChanged,
    totalData,
    totalPages,
  } = orderExportHook();

  useEffect(() => {
    if (dpDateRange[0] && dpDateRange[1]) {
      setIsInvalidDate(false);
    } else {
      setIsInvalidDate(true);
    }
  }, [dpDateRange]);

  const handleCalendarClose = () => {
    if (
      CommonUtility.isNotNull(dpDateRange[0]) &&
      CommonUtility.isNotEmpty(dpDateRange[1]) &&
      // @ts-expect-error - TS2339: Property 'getTime' does not exist on type 'never'.
      (previousDateRef.current?.[0]?.getTime?.() !==
        dpDateRange[0]?.getTime?.() ||
        // @ts-expect-error - TS2339: Property 'getTime' does not exist on type 'never'.
        previousDateRef.current?.[1]?.getTime?.() !==
          dpDateRange[1]?.getTime?.())
    ) {
      // @ts-expect-error - TS2322: Type 'Date[]' is not assignable to type 'null'.
      previousDateRef.current = dpDateRange; // Update for next comparison
    }
  };

  const columns = [
    {
      text: 'Exported Date',
      key: 'created',
      width: 3,
      textAlign: 'left',
    },
    {
      text: 'Start Date',
      key: 'start_date',
      width: 2,
      textAlign: 'left',
    },
    {
      text: 'End Date',
      key: 'end_date',
      width: 2,
      textAlign: 'left',
    },
    {
      text: 'Order Status',
      key: 'status',
      width: 2,
      textAlign: 'left',
    },
    {
      text: 'Status',
      key: 'status',
      width: 2,
      textAlign: 'left',
    },
    {
      text: 'File',
      key: 'file',
      textAlign: 'left',
      width: 2,
    },
  ];

  return (
    <Modal
      isOpen={open}
      onOpenChange={isOpen => !isOpen && onClose()}
      title="Export Orders"
      size="xl"
      showCloseButton
    >
      <p className="sk:mb-3">Select Date Range to export Orders</p>
      <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-2">
        <div className={cn('sk:w-80 sk:max-w-full', DATEPICKER_SCOPE)}>
          <DatePicker
            selectsRange
            dateFormat={DateFormat.datepicker}
            startDate={dpDateRange[0]}
            endDate={dpDateRange[1]}
            maxDate={new Date()}
            onCalendarClose={handleCalendarClose}
            onChange={update => {
              setIsInvalidDate(false);
              setDpDateRange(update);
            }}
            customInput={<RangeInput />}
          />
        </div>
        <Select
          aria-label="Order status"
          options={orderStatusOptions}
          value={exportStatus}
          onChange={setExportStatus}
          placeholder="All statuses"
          allowsEmptySelection
          className="sk:min-w-44"
        />
        <Button
          isDisabled={loading}
          onPress={() => {
            exportOrders(
              DateUtility.getUTCDateStartTime(dpDateRange[0]),
              DateUtility.getUTCDateEndTime(dpDateRange[1]),
              exportStatus,
            );
          }}
        >
          Fetch Orders Report
        </Button>
      </div>
      {isInvalidDate && <DangerText>Date is Invalid</DangerText>}
      <div className="sk:mt-6">
        <b>Recent Exports</b>
        <div className="sk:mb-2.5 sk:max-h-[45vh] sk:overflow-y-auto">
          <AppTable
            // @ts-expect-error - TS2322: Type '{ text: string; key: string; width: number; textAlign: string; }[]' is not...
            columns={columns}
            noShadow
            data={exportHistory}
            // @ts-expect-error - TS2741: Property 'item' is missing in type '{ fetchData: () => Promise<void>; }' but req...
            RowComponent={<FeedRow fetchData={fetchData} />}
            wrapperClass="sk:m-0!"
            loading={loading}
          />
        </div>
        <TablePagination
          noPadding
          colSpan={columns.length}
          currentPage={filter.page}
          totalPages={totalPages}
          pageChanged={pageChanged}
          pageSizeChanged={size => filterChanged('limit', size)}
          pageSize={filter.limit}
          totalData={totalData}
        />
      </div>
    </Modal>
  );
}

function FeedRow({ item, fetchData }) {
  return (
    <AppTableRow>
      <AppTableCell>
        {DateUtility.formatDate(item.createdAt, DateFormat.dateTime)}
      </AppTableCell>
      <AppTableCell>
        {DateUtility.formatDate(item.start_date, DateFormat.date)}
      </AppTableCell>
      <AppTableCell>
        {DateUtility.formatDate(item.end_date, DateFormat.date)}
      </AppTableCell>
      <AppTableCell>
        <div className="sk:flex! sk:items-center!">
          {item?.filters?.status ? (
            item?.filters?.status
          ) : (
            <span className="sk:text-center sk:block! sk:w-full">-</span>
          )}
        </div>
      </AppTableCell>
      <AppTableCell>
        <div className="sk:flex! sk:items-center!">
          {CommonUtility.removeUnderscoreAndCapitalize(item.status)}{' '}
          {['failed'].includes(item?.status) && (
            <DarkPopup
              trigger={<Info className="sk:ml-1" />}
              hoverable
              position="top center"
            >
              {item?.error}
            </DarkPopup>
          )}
        </div>
      </AppTableCell>
      <AppTableCell>
        {!['failed'].includes(item.status) &&
          (['pending'].includes(item.status) ? (
            <ArrowsClockwise
              size={20}
              className="sk:ml-2! sk:cursor-pointer"
              onClick={() => {
                fetchData();
              }}
            />
          ) : (
            <a
              target="_blank"
              rel="noreferrer"
              download
              href={item.url}
              className={buttonStyles({
                className: cn(
                  'sk:w-fit sk:gap-1.5 sk:no-underline',
                  !['completed'].includes(item.status) && 'sk:opacity-50',
                ),
              })}
            >
              <File size={16} className="sk:shrink-0" />
              <span>Download File</span>
            </a>
          ))}
      </AppTableCell>
    </AppTableRow>
  );
}
